import mixpanel from "mixpanel-browser";
import React, { createContext, PropsWithChildren, ReactNode, useCallback, useContext, useState } from "react";

import Button from "../components/design-system/Button";
import { Variant } from "../components/design-system/Button/Button";
import Dialog from "../components/design-system/Dialog";
import TextArea from "../components/design-system/TextArea";
import { SectionFooter, SectionHeader } from "../components/design-system/component-groups/section-header-footer";
import useStateWithInputChange from "./useStateWithInputChange";

interface AlertProps {
  title: string;
  prompt?: string;
  okVariant: Variant;
  okCaption: string;
  okAction?: (input: string) => void | null;
  cancelCaption?: string;
  cancelAction?: () => void | null;
  children: ReactNode;
}

const Context = createContext<(value: AlertProps) => void>(() => null);

export function AlertProvider({ children }: PropsWithChildren<unknown>) {
  const [alertBox, setAlertBox] = useState<AlertProps | null>(null);
  return (
    <Context.Provider value={setAlertBox}>
      {/* @ts-ignore - setAlertBox can accept null and TypeScript is wrong to insist it cannot. */}
      <AlertAndChildren alertBox={alertBox} setAlertBox={setAlertBox}>
        {children}
      </AlertAndChildren>
    </Context.Provider>
  );
}

function AlertAndChildren({
  children,
  alertBox,
  setAlertBox,
}: PropsWithChildren<{
  alertBox: AlertProps | null;
  setAlertBox: (value: AlertProps | null) => void;
}>) {
  const [input, changeInput, setInput] = useStateWithInputChange("");

  const cancel = useCallback(() => {
    alertBox!.cancelAction?.();
    setAlertBox(null);
    setInput("");
  }, [alertBox, setAlertBox, setInput]);

  const ok = useCallback(() => {
    alertBox!.okAction?.(input);
    setAlertBox(null);
    setInput("");
  }, [alertBox, input, setAlertBox, setInput]);

  return (
    <>
      {alertBox && (
        <Dialog size="small" isOpen alert>
          <SectionHeader title={alertBox.title} />
          {alertBox.children}
          {alertBox.prompt ? (
            <TextArea className="ds-mb-1" label={alertBox.prompt} onChange={changeInput} value={input} autoSize />
          ) : null}
          <SectionFooter>
            {alertBox.cancelCaption ? (
              <Button variant="ghost" onClick={cancel}>
                {alertBox.cancelCaption}
              </Button>
            ) : null}
            <Button variant={alertBox.okVariant || "primary"} onClick={ok}>
              {alertBox.okCaption}
            </Button>
          </SectionFooter>
        </Dialog>
      )}
      {children}
    </>
  );
}

export function useAlert() {
  const setAlertBox = useContext(Context);
  return useCallback(
    ({
      mixpanelName,
      ...props
    }: {
      title: string;
      okCaption: string;
      children: ReactNode;
      okVariant: Variant;
      mixpanelName?: string;
    }) =>
      new Promise<void>((resolve) => {
        if (mixpanelName) mixpanel.track(`Opened the '${mixpanelName}' alert modal`);
        setAlertBox({
          ...props,
          okAction: () => {
            if (mixpanelName) mixpanel.track(`Closed the '${mixpanelName}' alert modal`);
            resolve();
          },
        });
      }),
    [setAlertBox],
  );
}

export function useConfirm() {
  const setAlertBox = useContext(Context);
  return useCallback(
    ({
      mixpanelName,
      ...props
    }: {
      title: string;
      okCaption: string;
      okVariant: Variant;
      cancelCaption: string;
      children: ReactNode;
      mixpanelName?: string;
    }) =>
      new Promise<boolean>((resolve) => {
        if (mixpanelName) mixpanel.track(`Opened the '${mixpanelName}' confirm modal`);
        setAlertBox({
          ...props,
          okAction: () => {
            if (mixpanelName) mixpanel.track(`Confirmed the '${mixpanelName}' confirm modal`);
            resolve(true);
          },
          cancelAction: () => {
            if (mixpanelName) mixpanel.track(`Cancelled the '${mixpanelName}' confirm modal`);
            resolve(false);
          },
        });
      }),
    [setAlertBox],
  );
}

export function usePrompt() {
  const setAlertBox = useContext(Context);
  return useCallback(
    ({
      mixpanelName,
      ...props
    }: {
      title: string;
      prompt: string;
      okCaption: string;
      okVariant: Variant;
      cancelCaption?: string;
      children: ReactNode;
      mixpanelName?: string;
    }) =>
      new Promise<string | false>((resolve) => {
        if (mixpanelName) mixpanel.track(`Opened the '${mixpanelName}' confirm modal`);
        setAlertBox({
          ...props,
          okAction: (input: string) => {
            if (mixpanelName) mixpanel.track(`Confirmed the '${mixpanelName}' confirm modal`);
            resolve(input);
          },
          cancelAction: props.cancelCaption
            ? () => {
                if (mixpanelName) mixpanel.track(`Cancelled the '${mixpanelName}' confirm modal`);
                resolve(false);
              }
            : undefined,
        });
      }),
    [setAlertBox],
  );
}
